import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronRight, RefreshCw } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHead, makeToggle, type SortState } from "@/components/sortable-head"

interface Cycle {
  milestone_id: string
  contract_id: string
  label: string | null
  amount: number
  status: string
  expected_invoice_at: string | null
  contract_title: string | null
  entity_name: string | null
  window_from: string | null
  window_to: string | null
  hours_consumed: number | null
  hours_included: number | null
  effective_hourly_rate: number | null
}

interface ListResponse {
  success: boolean
  data?: { cycles: Cycle[] }
  error?: string
}

interface Props {
  shell: AppShell | null
}

type SortCol = "entity" | "contract" | "window" | "hours" | "amount" | "status"

const ALL_ENTITIES = "__all__"
const ALL_STATUSES = "__all_status__"

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "forecasted", label: "Pendentes" },
  { value: "invoiced", label: "Faturados" },
  { value: "paid", label: "Pagos" },
  { value: "overdue", label: "Vencidos" },
  { value: "cancelled", label: "Cancelados" },
  { value: ALL_STATUSES, label: "Todos os estados" },
]

const STATUS_LABEL: Record<string, string> = {
  forecasted: "Pendente",
  invoiced: "Faturado",
  paid: "Pago",
  overdue: "Vencido",
  cancelled: "Cancelado",
}

function fmtEur(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—"
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n)
}

function fmtHours(n: number | null | undefined): string {
  if (n == null) return "—"
  return `${n.toFixed(1)}h`
}

function fmtWindow(from: string | null, to: string | null): string {
  if (!from || !to) return "—"
  return `${from} → ${to}`
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "forecasted") return "secondary"
  if (status === "invoiced") return "default"
  if (status === "overdue") return "destructive"
  return "outline"
}

function compareCycles(a: Cycle, b: Cycle, sort: SortState<SortCol>): number {
  const dir = sort.dir === "asc" ? 1 : -1
  switch (sort.col) {
    case "entity":
      return ((a.entity_name ?? "").localeCompare(b.entity_name ?? "")) * dir
    case "contract":
      return ((a.contract_title ?? "").localeCompare(b.contract_title ?? "")) * dir
    case "window":
      return ((a.window_from ?? "").localeCompare(b.window_from ?? "")) * dir
    case "hours":
      return ((a.hours_consumed ?? -1) - (b.hours_consumed ?? -1)) * dir
    case "amount":
      return ((a.amount ?? 0) - (b.amount ?? 0)) * dir
    case "status":
      return a.status.localeCompare(b.status) * dir
  }
}

export function CyclesList({ shell }: Props) {
  const navigate = useNavigate()
  const [cycles, setCycles] = useState<Cycle[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [filterEntity, setFilterEntity] = useState<string>(ALL_ENTITIES)
  const [filterStatus, setFilterStatus] = useState<string>("forecasted")
  const [sort, setSort] = useState<SortState<SortCol>>({ col: "window", dir: "desc" })
  const toggleSort = makeToggle(setSort)

  async function load() {
    if (!shell) return
    setLoading(true)
    setError(null)
    try {
      const res = await shell.client.post<ListResponse>("actions/list_cycles", { params: {} })
      if (!res.success || !res.data) {
        setError(res.error ?? "Falha ao carregar ciclos")
        return
      }
      setCycles(res.data.cycles)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  // Cycles narrowed by the selected status first — the entity dropdown and
  // the table both work off this set so the counts stay consistent with what
  // is on screen.
  const statusFiltered = useMemo(() => {
    if (!cycles) return null
    if (filterStatus === ALL_STATUSES) return cycles
    return cycles.filter((c) => c.status === filterStatus)
  }, [cycles, filterStatus])

  // Distinct entities (with count) derived from the status-filtered cycles.
  // No second round-trip — keep the dropdown in sync with what is visible.
  // Sorted alphabetically; "Todos" is pinned on top.
  const entityOptions = useMemo(() => {
    if (!statusFiltered) return [] as Array<{ value: string; label: string; count: number }>
    const counts = new Map<string, number>()
    for (const c of statusFiltered) {
      const name = c.entity_name ?? "—"
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ value: name, label: name, count }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [statusFiltered])

  const visible = useMemo(() => {
    if (!statusFiltered) return null
    const filtered =
      filterEntity === ALL_ENTITIES
        ? statusFiltered
        : statusFiltered.filter((c) => (c.entity_name ?? "—") === filterEntity)
    return [...filtered].sort((a, b) => compareCycles(a, b, sort))
  }, [statusFiltered, filterEntity, sort])

  if (!shell) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Zenn Billing</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Standalone dev mode. Abre via <code className="font-mono">/pt/apps/zenn-billing</code>.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>
              {filterStatus === ALL_STATUSES
                ? "Ciclos"
                : `Ciclos · ${STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label ?? ""}`}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {filterStatus === "forecasted"
                ? "Recurring SLA cycles fechados que aguardam revisão e facturação."
                : "Histórico de ciclos por estado."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {entityOptions.length > 1 && (
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger className="h-9 w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_ENTITIES}>
                    Todos os clientes ({cycles?.length ?? 0})
                  </SelectItem>
                  {entityOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label} ({o.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "A actualizar…" : "Actualizar"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {visible == null && !error && (
            <p className="text-sm text-muted-foreground">A carregar ciclos…</p>
          )}

          {visible != null && visible.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {filterEntity === ALL_ENTITIES
                ? "Sem ciclos neste estado."
                : `Sem ciclos para ${filterEntity} neste estado.`}
            </p>
          )}

          {visible != null && visible.length > 0 && (
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead label="Cliente" col="entity" sort={sort} onClick={toggleSort} />
                    <SortableHead label="Contrato" col="contract" sort={sort} onClick={toggleSort} />
                    <SortableHead label="Janela" col="window" sort={sort} onClick={toggleSort} />
                    <SortableHead label="Horas" col="hours" sort={sort} onClick={toggleSort} align="right" />
                    <SortableHead label="Valor previsto" col="amount" sort={sort} onClick={toggleSort} align="right" />
                    <SortableHead label="Estado" col="status" sort={sort} onClick={toggleSort} />
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((c) => (
                    <TableRow
                      key={c.milestone_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/cycle/${c.contract_id}/${c.milestone_id}`)}
                    >
                      <TableCell className="font-medium">{c.entity_name ?? "—"}</TableCell>
                      <TableCell>{c.contract_title ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {fmtWindow(c.window_from, c.window_to)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtHours(c.hours_consumed)}
                        {c.hours_included != null && (
                          <span className="text-muted-foreground"> / {fmtHours(c.hours_included)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEur(c.amount)}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(c.status)}>
                          {STATUS_LABEL[c.status] ?? c.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

